import {Component, ElementRef, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs/Subject';
import {CustomerService} from '../../../services/customers.service';
import {Customer} from '../../../models/customer.model';
import {MachinesService} from '../../../services/machines.service';
import {Machine} from '../../../models/machine.model';
import {ConfirmationService} from 'primeng/primeng';
import {AuthenticationService} from '../../../services/authentication.service';
import {Maintenance} from '../../../models/maintenance.model';
import {MaintenanceService} from '../../../services/maintenance.service';
import {SelectItem} from 'primeng/components/common/selectitem';
import {Apointment} from '../../../models/apointment.model';
import {AgendaService} from '../../../services/agenda.service';

@Component({
  providers: [CustomerService, MachinesService, MaintenanceService, AgendaService],
  selector: 'app-customers-details',
  styleUrls: ['./customer.details.component.scss'],
  templateUrl: './customer.details.component.html',
})
export class CustomerDetailsComponent implements OnDestroy, OnInit {
  public customer: Customer;
  public machines: Machine[];
  public printMachines: Machine[];
  public showDeleted = false;

  // nieuwe afspraak
  public displayNewApointment = false;
  public newError: string;
  public maintenanceTypes: SelectItem[];
  public selectedMaintenance: string;
  public workhours = 0;
  public travelhours = 0;
  public traveldistance = 0;
  public newDate: Date;

  private _selectedMachine: Machine;
  private _initialMachines: Machine[];
  private _ngUnSubscribe: Subject<void> = new Subject<void>();

  constructor(private _route: ActivatedRoute, public router: Router, private _customerService: CustomerService,
              private _machineService: MachinesService, private _elementReference: ElementRef,
              private confirmationService: ConfirmationService, private _authService: AuthenticationService,
              private _maintenanceService: MaintenanceService, private _agendaService: AgendaService) {
  }

  public ngOnInit() {
    this.maintenanceTypes = [];
    this.maintenanceTypes.push({label: 'Regulier onderhoud', value: 'Regular'});
    this.maintenanceTypes.push({label: 'Incident', value: 'Incident'});

    this._route.params
      .takeUntil(this._ngUnSubscribe)
      .subscribe(params => {
        const customerId = params['id'];

        if (customerId) {
          this._customerService.load(customerId);
          this._customerService.customers$
            .takeUntil(this._ngUnSubscribe)
            .subscribe(
              customers => {
                this.customer = customers.find(pr => pr._id === customerId);
                this._machineService.loadAll(`?customer=${customerId}`);
              }
            );

          this._machineService.machines$
            .takeUntil(this._ngUnSubscribe)
            .subscribe(
              machines => {
                this._initialMachines = machines;
                this.updateMachinesList(!this.showDeleted);
              }
            );
        }
      });
  }

  public ngOnDestroy() {
    this._ngUnSubscribe.next();
    this._ngUnSubscribe.complete();
  }

  public toonVerwijderd(): void {
    this.showDeleted = !this.showDeleted;
    this.updateMachinesList(!this.showDeleted);
  }

  public historyCustomer(): void {
    this.router.navigate(['history'], {relativeTo: this._route});
  }

  public editCustomer(): void {
    this.router.navigate(['edit'], {relativeTo: this._route});
  }

  public deleteCustomer(): void {
    this.confirmationService.confirm({
      message: 'Weet u het zeker dat u de klant wilt verwijderen?',
      accept: () => {
        this._customerService.remove(this.customer._id)
          .subscribe(response => {
            this.router.navigate(['/customers']);
          });
      }
    });
  }

  public isAdmin(): boolean {
    if (this._authService.user && this._authService.user.role === 'admin') {
      return true;
    }

    return false;
  }

  set selectedMachine(value: Machine) {
    this._selectedMachine = value;
    this.detailsMachine();
  }
  get selectedMachine(): Machine {
    return this._selectedMachine;
  }

  private detailsMachine(): void {
    if (this.selectedMachine) {
      this.router.navigate(['/machines', this.selectedMachine._id]);
    }
  }

  public addMachine(): void {
    this.router.navigate(['/machines/new', {customerid: this.customer._id}]);
  }

  public printCustomer(divName): void {
    const printContents = document.getElementById(divName).innerHTML;
    const popupWin = window.open('', '_blank');
    popupWin.document.open()
    popupWin.document.write('<html><head></head><body onload="window.print()">' + printContents + '</html>');
    popupWin.document.close();
  }

  public isFinished(): boolean {
    if (!this.customer.currentMaintenance) {
      return true;
    }

    return this.customer.currentMaintenance.finished;
  }

  public isEdited(machine: Machine): boolean {
    if (this.isFinished()) {
      return false;
    }

    if(!machine.currentMachineMaintenance) {
      return false;
    }

    return (this.customer.currentMaintenance.machinemaintenances.indexOf(machine.currentMachineMaintenance) > -1);
  }

  public startMaintenance(): void {
    if (!this._authService.user) {
      return;
    }

    const newMaintenance: Maintenance = new Maintenance();
    newMaintenance.created = new Date();
    newMaintenance.finished = false;
    newMaintenance.customer = this.customer;
    newMaintenance.user = this._authService.user._id;

    this._maintenanceService.create(newMaintenance)
      .subscribe(maintenance => {
        if (maintenance) {
          this.customer.currentMaintenance = maintenance;
          this._customerService.update(this.customer);
        }
      });
  }

  public isIncident(): boolean {
    return (this.selectedMaintenance === 'Incident');
  }

  public finishMaintenance(): void {
    this.setApointmentSettings();
  }


  public saveNewApointment(): void {
    this.customer.currentMaintenance.finished = true;
    this.customer.currentMaintenance.workhours = this.workhours;
    this.customer.currentMaintenance.travelhours = this.travelhours;
    this.customer.currentMaintenance.distance = this.traveldistance;
    this.customer.currentMaintenance.name = this.selectedMaintenance;
    this._maintenanceService.update(this.customer.currentMaintenance)
      .subscribe(maintenance => {
        this._maintenanceService.sendMail(maintenance._id);

        if (this.selectedMaintenance === 'Regular') {
          const apointment = new Apointment();
          apointment.title = `${this.customer.name} onderhoud`;
          apointment.customer = this.customer;

          const day = this.newDate.getDate();
          let dayString = '';
          if (day < 10) {
            dayString = `0${day}`;
          } else {
            dayString = `${day}`;
          }
          const month = this.newDate.getMonth() + 1;
          let monthString = '';
          if (month < 10) {
            monthString = `0${month}`;
          } else {
            monthString = `${month}`;
          }

          const year = this.newDate.getFullYear();
          const dateString = `${year}-${monthString}-${dayString}T00:00:00.000Z`;

          apointment.date = dateString;
          this._agendaService.create(apointment);
        }

        this.setApointmentSettings();
        this.displayNewApointment = false;
      }, error => {
        this.setApointmentSettings();
        this.displayNewApointment = false;
      });
  }

  private setApointmentSettings(): void {
    this.displayNewApointment = true;
    this.selectedMaintenance = 'Regular';
    this.newError = null;
    this.workhours = 0;
    this.travelhours = 0;
    this.traveldistance = 0;
    this.newDate = new Date();
  }

  private updateMachinesList(showActiveOnly: boolean): void {
    this.machines = [];
    this.printMachines = [];
    this._initialMachines.forEach(machine => {
      if (machine.active) {
        this.printMachines.push(machine);
      }

      if (showActiveOnly && machine.active || !showActiveOnly) {
        this.machines.push(machine);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/customer/details/customer.details.component.ts